import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api.js';
import { renderBarChartRace } from './barChartRace.js';
import './BarChartRaceTab.css';

const LAST_GW = 38;

/* Squad-position colours, read from the tokens so the race matches the rest
 * of the site. The old code took them from a hardcoded map in the
 * controller. */
function positionColours() {
	const styles = getComputedStyle(document.documentElement);
	const read = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
	return {
		GKP: read('--color-pos-gkp', '#f0b400'),
		DEF: read('--color-pos-def', '#1c7ed6'),
		MID: read('--color-pos-mid', '#74b816'),
		FWD: read('--color-pos-fwd', '#f76707'),
	};
}

export default function BarChartRaceTab({ season }) {
	const containerRef = useRef(null);
	const stopRef = useRef(null);
	const [data, setData] = useState(null);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);
	const [range, setRange] = useState({ start: 1, end: LAST_GW });
	const [runId, setRunId] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setStatus('loading');
		api
			.get(`/api/history/barchartrace/${season}`)
			.then((payload) => {
				if (cancelled) return;
				if (!payload || !Object.keys(payload).length) {
					setError('No bar chart race data recorded for this season.');
					setStatus('error');
					return;
				}
				const colours = positionColours();
				Object.values(payload).forEach((entry) => {
					entry.color = colours[entry.pos] ?? colours.MID;
				});
				const length = Object.values(payload)[0]?.data?.length ?? LAST_GW + 1;
				setData(payload);
				setRange({ start: 1, end: Math.max(1, length - 1) });
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load the race.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, [season]);

	useEffect(() => {
		if (status !== 'ready' || !data || !containerRef.current) return undefined;
		stopRef.current?.();
		stopRef.current = renderBarChartRace(containerRef.current, {
			data,
			timelineStart: range.start,
			timelineEnd: range.end,
			title: `${season} season points`,
			timelineLabel: (_start, current) => `GW ${current}`,
		});
		/* Always cancel the timer on unmount or replay — d3.interval keeps
		 * firing into a detached node otherwise. */
		return () => stopRef.current?.();
	}, [status, data, season, range.start, range.end, runId]);

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading race…</span>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load the race.</strong>
				<span>{error}</span>
			</div>
		);
	}

	const clamp = (value) => Math.min(LAST_GW, Math.max(1, Number(value) || 1));

	return (
		<div className="bar-race">
			<div className="bar-race__controls">
				<label className="bar-race__field">
					<span className="field__label">From GW</span>
					<input
						className="field__input"
						type="number"
						min="1"
						max={LAST_GW}
						value={range.start}
						onChange={(e) =>
							setRange((r) => ({ ...r, start: Math.min(clamp(e.target.value), r.end) }))
						}
					/>
				</label>
				<label className="bar-race__field">
					<span className="field__label">To GW</span>
					<input
						className="field__input"
						type="number"
						min="1"
						max={LAST_GW}
						value={range.end}
						onChange={(e) =>
							setRange((r) => ({ ...r, end: Math.max(clamp(e.target.value), r.start) }))
						}
					/>
				</label>
				<button
					className="btn btn--primary"
					type="button"
					onClick={() => setRunId((n) => n + 1)}
				>
					Replay
				</button>
			</div>

			<div className="bar-race__chart" ref={containerRef} />
		</div>
	);
}
